import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import * as pdfjsLib from "pdfjs-dist/webpack";
import useApi from "./useApi";
import useData from "./useData";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const useDocument = () => {
  const { getData, postData, retrieveFile, uploadFile } = useApi();
  const { findOne } = useData();

  const [documents, setDocuments] = useState(null);
  const [documentUrl, setDocumenturl] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const recordCount = Array.isArray(documents) ? documents.length : 0;

  const getDocuments = async (scope, id) => {
    let filter = { scope, folder: "documents" };

    if (scope === "client") filter.clientId = id;
    if (scope === "project") filter.projectId = id;

    const docs = await getData({
      entityName: "Document",
      filter: filter,
    });

    if (docs.isSuccessful) {
      setDocuments(docs.data);
    } else {
      setDocuments([]);
    }
  };

  const getEntityName = async (scope, id) => {
    if (scope === "global") return "Global";

    const entityData = await getData({
      entityName: scope,
      id,
    });

    if (entityData.isSuccessful) {
      return entityData.name;
    }

    return "Unknown";
  };

  const selectDocument = async (id) => {
    setSelectedDocument(null);
    const doc = findOne(documents, { id }) || findOne(documents, { key: id });
    if (!doc) return;
    setSelectedDocument(doc);

    await getDocumentUrl(`${doc.key}`);
  };

  const getDocumentUrl = async (key) => {
    const url = await retrieveFile(key);

    setDocumenturl(url);
  };

  const deleteDocument = async (id) => {
    const result = await getData({
      entityName: "Document",
      action: "delete",
      id: id,
    });
    return result;
  };

  const updateDocumentMetadata = async (document, updates = {}) => {
    const key = document?.key;
    const normalizedFolderPath = String(
      updates.folderPath ?? updates.category ?? "",
    )
      .trim()
      .replace(/\\/g, "/")
      .replace(/\/+/g, "/")
      .replace(/^\/+|\/+$/g, "");

    if (!key) {
      return {
        isSuccessful: false,
        message: "Document key is required",
      };
    }

    return postData({
      entityName: "Document",
      action: "updateMetadata",
      data: {
        metadata: {
          key,
          name: updates.name,
          description: updates.description,
          folderPath: normalizedFolderPath,
          category: normalizedFolderPath,
        },
      },
    });
  };

  const keyWithoutFolder = (fullKey) => {
    const key = String(fullKey || "").trim();
    if (!key) return "";

    if (key.startsWith("documents/")) {
      return key.substring("documents/".length);
    }

    return key;
  };

  const isPdfFile = (file) => {
    const mimeType = String(file?.type || "").toLowerCase();
    const filename = String(file?.name || "").toLowerCase();
    return mimeType === "application/pdf" || filename.endsWith(".pdf");
  };

  const replaceDocumentFile = async (docRecord, file, updates = {}) => {
    const docKey = String(docRecord?.key || "").trim();
    if (!docKey) {
      return { isSuccessful: false, message: "Document key is required" };
    }

    if (!file) {
      return { isSuccessful: false, message: "Replacement file is required" };
    }

    const incomingExtension = file.name?.includes(".")
      ? file.name.substring(file.name.lastIndexOf("."))
      : "";
    const existingExtension = String(docRecord?.extension || "").trim();
    if (
      incomingExtension &&
      existingExtension &&
      incomingExtension.toLowerCase() !== existingExtension.toLowerCase()
    ) {
      return {
        isSuccessful: false,
        message: `Replacement must use ${existingExtension} files to preserve document identity`,
      };
    }

    const key = keyWithoutFolder(docKey);
    const nextName = updates.name ?? docRecord?.name ?? "";
    const nextDescription = updates.description ?? docRecord?.description ?? "";
    const nextFolderPath = String(
      updates.folderPath ?? docRecord?.folderPath ?? docRecord?.category ?? "",
    )
      .trim()
      .replace(/\\/g, "/")
      .replace(/\/+/g, "/")
      .replace(/^\/+|\/+$/g, "");

    const cleanupResults = await postData({
      entityName: "Document",
      action: "cleanupConvertedPages",
      data: { parentKey: docKey },
    });

    if (!cleanupResults?.isSuccessful) {
      return {
        isSuccessful: false,
        message: cleanupResults?.message || "Failed to cleanup existing pages",
      };
    }

    const baseMetadata = {
      key,
      folder: "documents",
      extension: file.name?.includes(".")
        ? file.name.substring(file.name.lastIndexOf("."))
        : docRecord?.extension || "",
      originalName: file.name,
      mimeType: file.type || docRecord?.mimeType,
      size: file.size,
      scope: docRecord?.scope,
      clientId: docRecord?.scope === "client" ? docRecord?.clientId : null,
      projectId: docRecord?.scope === "project" ? docRecord?.projectId : null,
      tags: Array.isArray(docRecord?.tags) ? docRecord.tags : [],
      name: nextName,
      description: nextDescription,
      folderPath: nextFolderPath,
      category: nextFolderPath,
      uploadedAt: new Date(),
    };

    let uploadResult;
    try {
      uploadResult = await uploadFile(file, baseMetadata);
    } catch (err) {
      return {
        isSuccessful: false,
        message: err?.message || "Failed to upload replacement file",
      };
    }

    if (isPdfFile(file)) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 2.0 });

          const canvas = window.document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({ canvasContext: context, viewport }).promise;

          const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/png"),
          );

          const pageMetadata = {
            key: `${uuidv4()}`,
            folder: "converted",
            extension: ".png",
            originalName: `page-${i}.png`,
            mimeType: "image/png",
            size: blob?.size || 0,
            scope: docRecord?.scope,
            clientId:
              docRecord?.scope === "client" ? docRecord?.clientId : null,
            projectId:
              docRecord?.scope === "project" ? docRecord?.projectId : null,
            tags: Array.isArray(docRecord?.tags) ? docRecord.tags : [],
            name: `${nextName || docRecord?.name || "Document"} - Page ${i}`,
            description: nextDescription,
            folderPath: nextFolderPath,
            category: nextFolderPath,
            uploadedAt: new Date(),
            pageNumber: i,
            parentKey: docKey,
          };

          await uploadFile(blob, pageMetadata);
        }
      } catch (err) {
        return {
          isSuccessful: false,
          message: err?.message || "Failed to rebuild PDF pages",
        };
      }
    }

    return {
      isSuccessful: true,
      data: uploadResult,
    };
  };

  return {
    getDocuments,
    documents,
    getEntityName,
    recordCount,
    getDocumentUrl,
    documentUrl,
    selectDocument,
    selectedDocument,
    deleteDocument,
    updateDocumentMetadata,
    replaceDocumentFile,
  };
};

export default useDocument;
